<script setup lang="ts">
const STICKY_AD_UNIT_ID = '/21658289790,22466679900/glouton_application'
const REFRESH_INTERVAL_MS = 30_000

const baseStore = useBaseStore()
const userStore = useUserStore()
const consentStore = useConsentStore()

let syncGen = 0

const shouldShow = computed(() => {
  if (!baseStore.nativeStickyAllowedBySplash)
    return false
  if (!baseStore.showAds || !baseStore.showNativeStickyAd)
    return false
  if (consentStore.preferencesOpen && consentStore.preferencesScreen === 'summary')
    return false
  if (baseStore.crispChatOpen)
    return false
  if (userStore.user && Number(userStore.user.extra) === 1)
    return false
  return true
})

function getBottomBarHeight(): number {
  const el = document.getElementById('bottom-bar')
  return el ? Math.round(el.getBoundingClientRect().height) : 68
}

function getBottomOffset(): number {
  if (baseStore.modalOpenCount > 0)
    return 0
  return getBottomBarHeight()
}

async function showBanner() {
  if (!import.meta.client || !shouldShow.value)
    return
  const bottomOffset = getBottomOffset()
  try {
    const { AdManager } = await import('~/utils/ad-manager')
    if (!shouldShow.value) {
      await AdManager.hideStickyBanner()
      return
    }
    await AdManager.showStickyBanner({ adUnitId: STICKY_AD_UNIT_ID, bottomOffset })
  }
  catch (err) {
    console.warn('[GAMStickyBannerAd] Failed to show sticky banner:', err)
  }
}

async function updateBannerOffset() {
  if (!shouldShow.value || !import.meta.client)
    return
  const bottomOffset = getBottomOffset()
  try {
    const { AdManager } = await import('~/utils/ad-manager')
    await AdManager.updateStickyBannerBottomOffset({ bottomOffset })
  }
  catch (err) {
    console.warn('[GAMStickyBannerAd] Failed to update sticky banner offset:', err)
  }
}

async function hideBanner() {
  if (!import.meta.client)
    return
  try {
    const { AdManager } = await import('~/utils/ad-manager')
    await AdManager.hideStickyBanner()
  }
  catch (err) {
    console.warn('[GAMStickyBannerAd] Failed to hide sticky banner:', err)
  }
}

let resizeObserver: ResizeObserver | null = null
let refreshTimer: ReturnType<typeof setInterval> | null = null

function stopObservers() {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
  resizeObserver?.disconnect()
  resizeObserver = null
}

function startObservers() {
  stopObservers()
  if (!import.meta.client)
    return
  const bottomBar = document.getElementById('bottom-bar')
  if (bottomBar) {
    resizeObserver = new ResizeObserver(() => {
      updateBannerOffset()
    })
    resizeObserver.observe(bottomBar)
  }
  refreshTimer = setInterval(async () => {
    if (!shouldShow.value)
      return
    const gen = syncGen
    await hideBanner()
    if (!shouldShow.value || gen !== syncGen)
      return
    await showBanner()
  }, REFRESH_INTERVAL_MS)
}

watch(shouldShow, async (visible) => {
  const gen = ++syncGen
  if (!visible) {
    stopObservers()
    await hideBanner()
    return
  }
  await showBanner()
  if (gen !== syncGen)
    return
  await nextTick()
  startObservers()
}, { immediate: true })

watch(() => baseStore.modalOpenCount, async () => {
  if (shouldShow.value && import.meta.client)
    await updateBannerOffset()
})

onBeforeUnmount(async () => {
  syncGen++
  stopObservers()
  await hideBanner()
})
</script>

<template>
  <div v-show="false" aria-hidden="true" />
</template>
